import React, { createContext, useContext, useState, useEffect } from 'react';

const ADMIN = 0;
const USER = 1;
const STUDENT = 2;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsSubscription, setNeedsSubscription] = useState(true);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isStudent, setIsStudent] = useState(false);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }
    
    try {
      // Validate token with your backend endpoint
      const response = await fetch(process.env.REACT_APP_API_URL + '/auth/validate', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const userType = await response.json(); // Get the actual response data
        
        if (userType === USER) {
          setIsAuthenticated(true);
          setIsStudent(false); // Explicitly set to false for regular users
          
          // After confirming authentication, check subscription status
          await checkSubscriptionStatus();
        }
        else if (userType === STUDENT) {
          setIsStudent(true);
          setIsAuthenticated(true); // Students are also authenticated users
          setNeedsSubscription(false); // Students don't need subscriptions
          // Maybe load student-specific data here instead of checkSubscriptionStatus
        }
        else if (userType === ADMIN) {
          setIsAuthenticated(true);
          setIsStudent(false);
          setNeedsSubscription(false); // Admins don't need subscriptions
        }
       
      } else {
        // If API call fails, assume not authenticated
        setIsAuthenticated(false);
        setIsStudent(false);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('tutor_id');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setIsAuthenticated(false);
      setIsStudent(false);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('tutor_id');
    }
    
    setLoading(false);
  };

  const checkSubscriptionStatus = async () => {
    try {
      const userData = await fetchProtectedData();
      
      // Check for null, undefined, or false values
      if (userData.tutor.id === undefined || userData.tutor.id === null || userData.active === false || userData.active === undefined) {
        console.log("User needs subscription - tutorId or active check failed");
        setNeedsSubscription(true);
        return;
      }
      
      localStorage.setItem("tutor_id", userData.tutor.id.toString());
      setNeedsSubscription(false);
      setUser(userData);
      console.log("User subscription check successful");
    } catch (err) {
      console.error("Failed to fetch tutor data:", err);
      // On error, assume they need subscription rather than logging out
      setNeedsSubscription(true);
    }
  };

  const fetchProtectedData = async () => {
    const token = localStorage.getItem("auth_token");
    const userDataFromStorage = localStorage.getItem("user_data");
    
    // Safe JSON parsing
    if (!userDataFromStorage) {
      throw new Error("No user data found in localStorage");
    }
    
    let email;
    try {
      const parsedUserData = JSON.parse(userDataFromStorage);
      email = parsedUserData.email;
      if (!email) {
        throw new Error("No email found in user data");
      }
    } catch (parseError) {
      throw new Error("Invalid user data format in localStorage");
    }

    const response = await fetch(process.env.REACT_APP_API_URL + `/users/byEmail/me`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    // Check if the response is successful
    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const apiUserData = await response.json();
    
    // Validate the user object has required properties
    if (!apiUserData || typeof apiUserData !== 'object') {
      throw new Error("Invalid user data received from server");
    }
    
    return apiUserData;
  };

  const login = async (credentials) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/authenticate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }
      
      const data = await response.json();
      const token = data.token;

      // Store the token and user data
      localStorage.setItem('auth_token', token);
      
      const userData = {
        email: credentials.email,
      };

      localStorage.setItem('user_data', JSON.stringify(userData));
      
      // After login, check what type of user this is
      await checkAuth();
      
      return { token, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear all auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('tutor_id');
    
    // Update authentication state
    setIsAuthenticated(false);
    setNeedsSubscription(true);
    setUser(null);
    setIsStudent(false); // Reset student state on logout
  };

  const refreshSubscriptionStatus = async () => {
    // After successful subscription, wait a moment for backend to update
    // then re-check subscription status
    setTimeout(async () => {
      await checkSubscriptionStatus();
    }, 2000); // Wait 2 seconds for backend to process
  };

  const value = {
    isAuthenticated,
    needsSubscription,
    loading,
    user,
    isStudent,
    login,
    logout,
    checkAuth,
    refreshSubscriptionStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};